import React, { useState, useEffect, useCallback } from 'react';
import { 
	Box, 
	Paper, 
	Typography, 
	CircularProgress,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Chip,
	IconButton,
	Tooltip
} from '@mui/material';
import { 
	Visibility as VisibilityIcon,
	Delete as DeleteIcon 
} from '@mui/icons-material';
import { IManagerInitiationData } from '../interfaces/IOnboardingFlow';
import {
	ManagerInitiationForm,
} from '../components';
import style from './OnboardingDashboard.module.scss';
import { createOnboarding, getOnboardings, deleteOnboarding, OnboardingRecord } from 'core/API/onboarding';
import { useToastContext } from 'core/components/Toast/context/ToastContext';
import { usePermissionCheck } from 'core/hooks/usePermissionCheck';
import { Button } from '../../../components/ui/button';

interface OnboardingDashboardProps {
	workplaceId: string;
	organizationId: string;
}

export default function OnboardingDashboard({
	workplaceId,
	organizationId: _organizationId,
}: OnboardingDashboardProps): JSX.Element {
	const [onboardingRecords, setOnboardingRecords] = useState<OnboardingRecord[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [currentView, setCurrentView] = useState<
		'dashboard' | 'create' | 'employee-entry' | 'manager-review'
	>('dashboard');
	const [_selectedRecord, setSelectedRecord] = useState<OnboardingRecord | null>(null);
	const [_totalItems, setTotalItems] = useState(0);
	const [page, _setPage] = useState(1);
	const [itemsPerPage] = useState(30);
	const { showToast } = useToastContext();

	// Permission checks for Onboarding module
	const { hasModuleExecutePermission, hasModuleDeletePermission } = usePermissionCheck();
	const canExecuteOnboarding = hasModuleExecutePermission('Onboarding');
	const canDeleteOnboarding = hasModuleDeletePermission('Onboarding');

	// Fetch onboarding records
	const fetchOnboardingRecords = useCallback(async () => {
		setIsLoading(true);
		try {
			const response = await getOnboardings(Number(workplaceId), page, itemsPerPage);
			const records = response.member || response['hydra:member'] || [];
			const total = response.totalItems || response['hydra:totalItems'] || 0;
			
			setOnboardingRecords(records);
			setTotalItems(total);
		} catch (error) {
			console.error('Error fetching onboarding records:', error);
			showToast('error', 'Failed to fetch onboarding records');
		} finally {
			setIsLoading(false);
		}
	}, [workplaceId, page, itemsPerPage, showToast]);

	useEffect(() => {
		fetchOnboardingRecords();
	}, [fetchOnboardingRecords]);

	// Handler for creating new onboarding
	const handleCreateOnboarding = () => {
		setCurrentView('create');
		setSelectedRecord(null);
	};

	// Handler for submitting Phase 1 (Manager Initiation)
	const handleSubmitManagerInitiation = async (data: IManagerInitiationData) => {
		try {
			// Map form data to API payload
			const payload = {
				user: data.existingEmployeeId || undefined, // Optional, for existing/rehire employees
				firstName: data.firstName,
				middleName: data.middleName || '',
				lastName: data.lastName,
				email: data.email,
				cellPhone: data.cellPhone,
				type: data.employeeType, // 'new', 'rehire', 'existing'
				workplace: `/api/workplaces/${workplaceId}`,
				employmentStartDate: new Date(data.employmentStartDate).toISOString(),
				payType: data.roleType, // 'part_time' or 'full_time'
				employmentType: data.employmentType, // 'hourly' or 'salary'
				securityRole: data.securityRole, // Already an IRI
			};

			// Call API to create onboarding
			const response = await createOnboarding(payload);
			console.log('Onboarding created successfully:', response);
			
			showToast('success', 'Onboarding created successfully!');
			
			// Refresh the list
			await fetchOnboardingRecords();
			
			// Return to dashboard
			setCurrentView('dashboard');
		} catch (error: any) {
			console.error('Error creating onboarding record:', error);
			showToast(
				'error',
				error?.response?.data?.message || 'Failed to create onboarding record. Please try again.'
			);
		}
	};

	// Handler for opening a record for review/edit
	const handleOpenRecord = (record: OnboardingRecord) => {
		setSelectedRecord(record);
		// For now, just log the record - implement view logic as needed
		console.log('Opening onboarding record:', record);
	};

	// Handler for deleting a record
	const handleDeleteRecord = async (id: number) => {
		if (!window.confirm('Are you sure you want to delete this onboarding record?')) {
			return;
		}
		
		try {
			await deleteOnboarding(id);
			showToast('success', 'Onboarding record deleted successfully');
			await fetchOnboardingRecords();
		} catch (error: any) {
			console.error('Error deleting onboarding record:', error);
			showToast('error', 'Failed to delete onboarding record');
		}
	};

	// Handler for canceling form
	const handleCancelForm = () => {
		setCurrentView('dashboard');
		setSelectedRecord(null);
	};

	// Get status chip color
	const getStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' => {
		switch (status?.toLowerCase()) {
			case 'completed':
				return 'success';
			case 'in_progress':
				return 'primary';
			case 'pending':
				return 'warning';
			case 'draft':
				return 'default';
			default:
				return 'default';
		}
	};

	// Format date for display
	const formatDate = (dateString: string | undefined) => {
		if (!dateString) return 'N/A';
		try {
			return new Date(dateString).toLocaleDateString('en-US', {
				year: 'numeric',
				month: 'short',
				day: 'numeric'
			});
		} catch {
			return 'Invalid Date';
		}
	};

	return (
		<div className={style.DashboardContainer}>
			{/* Header - only show on dashboard view */}
			{currentView === 'dashboard' && canExecuteOnboarding && (
				<div className={style.Header}>
					<Button onClick={handleCreateOnboarding} className={style.CreateButton}>
						Add
					</Button>
				</div>
			)}

			{/* Dashboard View */}
			{currentView === 'dashboard' && (
				<>
					{isLoading ? (
						<Box sx={{ textAlign: 'center', padding: '3rem' }}>
							<CircularProgress />
						</Box>
					) : (
						<>
							{/* In Progress Section */}
							<Paper sx={{ p: 3, mb: 3 }}>
								<Box sx={{ mb: 2 }}>
									<Typography variant="h6">In Progress ({onboardingRecords.filter(r => r.status?.toLowerCase() !== 'completed' && r.status?.toLowerCase() !== 'archived').length})</Typography>
								</Box>
								{onboardingRecords.filter(r => r.status?.toLowerCase() !== 'completed' && r.status?.toLowerCase() !== 'archived').length === 0 ? (
									<Box sx={{ textAlign: 'center', padding: '2rem' }}>
										<Typography sx={{ color: '#999' }}>No onboarding in progress</Typography>
									</Box>
								) : (
						<TableContainer sx={{ maxHeight: 'calc(100vh - 300px)' }}>
							<Table stickyHeader>
								<TableHead>
									<TableRow>
										<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Name</TableCell>
										<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Email</TableCell>
										<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Phone</TableCell>
										<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Type</TableCell>
										<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Status</TableCell>
										<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Employment Start</TableCell>
										<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Pay Type</TableCell>
										<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Employment Type</TableCell>
									<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', textAlign: 'center', width: '120px', minWidth: '120px' }}>Actions</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{onboardingRecords.filter(r => r.status?.toLowerCase() !== 'completed' && r.status?.toLowerCase() !== 'archived').map((record) => (
										<TableRow
											key={record.id}
											sx={{ '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
										>
											<TableCell>
												{`${record.firstName} ${record.middleName ? record.middleName + ' ' : ''}${record.lastName}`}
											</TableCell>
											<TableCell>{record.email}</TableCell>
											<TableCell>{record.cellPhone}</TableCell>
											<TableCell>
											<Chip label={record.type} size="small" />
											</TableCell>
											<TableCell>
												<Chip 
													label={record.status.replace('_', ' ').toUpperCase()} 
													size="small" 
													color={getStatusColor(record.status)}
												/>
											</TableCell>
											<TableCell>{formatDate(record.employmentStartDate)}</TableCell>
											<TableCell>
											<Chip label={record.payType} size="small" />
										</TableCell>
										<TableCell>
											<Chip label={record.employmentType} size="small" />
											</TableCell>
										<TableCell sx={{ textAlign: 'center', width: '120px', minWidth: '120px' }}>
												<Tooltip title="View Details">
													<IconButton
														size="small"
														onClick={() => handleOpenRecord(record)}
														sx={{ color: 'rgb(68, 99, 136)' }}
													>
														<VisibilityIcon fontSize="small" />
													</IconButton>
												</Tooltip>
												{canDeleteOnboarding && (
													<Tooltip title="Delete">
														<IconButton
															size="small"
															onClick={() => handleDeleteRecord(record.id)}
															sx={{ color: '#d32f2f' }}
														>
															<DeleteIcon fontSize="small" />
														</IconButton>
													</Tooltip>
												)}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					)}
				</Paper>

				{/* Archive Section */}
				<Paper sx={{ p: 3 }}>
					<Box sx={{ mb: 2 }}>
						<Typography variant="h6">Archive ({onboardingRecords.filter(r => r.status?.toLowerCase() === 'completed' || r.status?.toLowerCase() === 'archived').length})</Typography>
					</Box>
					{onboardingRecords.filter(r => r.status?.toLowerCase() === 'completed' || r.status?.toLowerCase() === 'archived').length === 0 ? (
						<Box sx={{ textAlign: 'center', padding: '2rem' }}>
							<Typography sx={{ color: '#999' }}>No completed onboarding records</Typography>
						</Box>
					) : (
						<TableContainer sx={{ maxHeight: 'calc(100vh - 300px)' }}>
							<Table stickyHeader>
								<TableHead>
									<TableRow>
										<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Name</TableCell>
										<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Email</TableCell>
										<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Phone</TableCell>
										<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Type</TableCell>
										<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Status</TableCell>
										<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Employment Start</TableCell>
										<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Pay Type</TableCell>
										<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Employment Type</TableCell>
										<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', textAlign: 'center', width: '120px', minWidth: '120px' }}>Actions</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{onboardingRecords.filter(r => r.status?.toLowerCase() === 'completed' || r.status?.toLowerCase() === 'archived').map((record) => (
										<TableRow
											key={record.id}
											sx={{ '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
										>
											<TableCell>
												{`${record.firstName} ${record.middleName ? record.middleName + ' ' : ''}${record.lastName}`}
											</TableCell>
											<TableCell>{record.email}</TableCell>
											<TableCell>{record.cellPhone}</TableCell>
											<TableCell>
												<Chip label={record.type} size="small" />
											</TableCell>
											<TableCell>
												<Chip 
													label={record.status.replace('_', ' ').toUpperCase()} 
													size="small" 
													color={getStatusColor(record.status)}
												/>
											</TableCell>
											<TableCell>{formatDate(record.employmentStartDate)}</TableCell>
											<TableCell>
												<Chip label={record.payType} size="small" />
											</TableCell>
											<TableCell>
												<Chip label={record.employmentType} size="small" />
											</TableCell>
											<TableCell sx={{ textAlign: 'center', width: '120px', minWidth: '120px' }}>
												<Tooltip title="View Details">
													<IconButton
														size="small"
														onClick={() => handleOpenRecord(record)}
														sx={{ color: 'rgb(68, 99, 136)' }}
													>
														<VisibilityIcon fontSize="small" />
													</IconButton>
												</Tooltip>
												{canDeleteOnboarding && (
													<Tooltip title="Delete">
														<IconButton
															size="small"
															onClick={() => handleDeleteRecord(record.id)}
															sx={{ color: '#d32f2f' }}
														>
															<DeleteIcon fontSize="small" />
														</IconButton>
													</Tooltip>
												)}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					)}
				</Paper>
			</>
			)}
		</>
		)}

		{/* Phase 1: Manager Initiation Form */}
			{currentView === 'create' && (
				<ManagerInitiationForm
					workplaceId={workplaceId}
					onSubmit={handleSubmitManagerInitiation}
					onCancel={handleCancelForm}
				/>
			)}

			{/* Phase 2 & 3: To be implemented with new API structure */}
			{/* {currentView === 'employee-entry' && selectedRecord && (
				<EmployeeDataEntryView
					record={selectedRecord}
					onClose={handleCancelForm}
				/>
			)} */}

			{/* {currentView === 'manager-review' && selectedRecord && (
				<ManagerReviewView
					record={selectedRecord}
					onComplete={async () => {
						await fetchOnboardingRecords();
						setCurrentView('dashboard');
						setSelectedRecord(null);
					}}
					onCancel={handleCancelForm}
				/>
			)} */}
		</div>
	);
}
